import { useId, useState } from 'react';
import type { AnyEvent, ItemRecord } from '@intentionalist/shared';
import { anyEvent, orderedPhotos, uuidv7 } from '@intentionalist/shared';
import { deviceId, tick } from './clock.js';
import { capture, PhotoError } from './photos.js';
import { PhotoSourcePicker } from './PhotoSourcePicker.js';
import { usePhotoSources } from './usePhotoSources.js';
import type { ReplicaDb } from './db.js';

/**
 * An item's photos (FR-2.3, FR-2.4, FR-2.7).
 *
 * Up to ten, captured or chosen from storage. A thumbnail is shown from the local
 * blob the moment it is captured, before anything is uploaded, so the photo is
 * there whether or not there is a network. Only photos this device did not capture
 * need a signed URL, and those are fetched in one batch.
 */
export const MAX_PHOTOS = 10;

function event(type: string, itemId: string, payload: unknown): AnyEvent {
  const at = Date.now();
  return anyEvent.parse({
    eventId: uuidv7(at),
    hlc: tick(at),
    deviceId: deviceId(),
    entityId: itemId,
    clientTs: new Date(at).toISOString(),
    type,
    payload,
  });
}

export function PhotoStrip({
  db,
  userId,
  item,
  onWrite,
}: {
  db: ReplicaDb;
  userId: string;
  item: ItemRecord;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const headingId = useId();

  // Ordering and the primary fallback are domain rules, not view logic, so they
  // live in the shared module beside the merge that maintains them.
  const ordered = orderedPhotos(item);

  /** Every source is closed once the item is at its limit (FR-2.3). */
  const full = busy || ordered.length >= MAX_PHOTOS;
  const sources = usePhotoSources(
    db,
    ordered.map((photo) => ({
      photoId: photo.photoId,
      derivativeKey: photo.metadata?.v.derivativeKey,
    })),
  );

  async function add(photos: Blob[]) {
    setError(null);
    setBusy(true);
    try {
      const room = MAX_PHOTOS - ordered.length;
      if (room <= 0) {
        setError(`An item can have at most ${MAX_PHOTOS} photos.`);
        return;
      }

      const events: AnyEvent[] = [];
      for (const file of photos.slice(0, room)) {
        const { photoId, event: added } = await capture({ db, userId, itemId: item.itemId, file });
        events.push(added);
        // FR-2.4: the first photo attached becomes primary without being asked.
        if (ordered.length === 0 && events.length === 1) {
          events.push(event('photo.primarySet', item.itemId, { photoId }));
        }
      }
      if (photos.length > room) {
        setError(
          `Only ${room} more photo${room === 1 ? '' : 's'} would fit; the rest were skipped.`,
        );
      }
      onWrite(events);
    } catch (caught) {
      setError(caught instanceof PhotoError ? caught.message : 'That photo could not be added.');
    } finally {
      setBusy(false);
    }
  }

  const move = (photoId: string, by: -1 | 1) => {
    const current = ordered.map((p) => p.photoId);
    const from = current.indexOf(photoId);
    const to = from + by;
    if (to < 0 || to >= current.length) return;
    [current[from], current[to]] = [current[to]!, current[from]!];
    // A full ordering rather than a swap, so the result cannot depend on the
    // order two devices' events arrive in.
    onWrite([event('photo.reordered', item.itemId, { photoIds: current })]);
  };

  return (
    <section aria-labelledby={headingId} className="photos">
      <h3 id={headingId}>Photos</h3>

      <PhotoSourcePicker disabled={full} onChoose={(photos) => add(photos)} onError={setError} />

      <p className="warn" role="alert" aria-live="polite">
        {error ?? ''}
      </p>

      {ordered.length === 0 ? (
        <p className="muted">No photos yet.</p>
      ) : (
        <ul className="photo-grid" aria-label="Photos">
          {ordered.map((photo, index) => {
            const primary = item.primaryPhotoId?.v === photo.photoId;
            const uploaded = photo.uploaded?.v === true;
            return (
              <li key={photo.photoId}>
                {sources[photo.photoId] === undefined ? (
                  <div className="thumb muted" role="img" aria-label="Photo not available offline">
                    ?
                  </div>
                ) : (
                  <img
                    className="thumb"
                    src={sources[photo.photoId]}
                    alt={`Photo ${index + 1}${primary ? ', primary' : ''}`}
                  />
                )}

                {/*
                  Three groups rather than four loose buttons.
                  They were a flat row of unequal widths in a tile too narrow to
                  hold them, so they wrapped wherever they happened to run out --
                  "Primary" and one arrow on the first line, the other arrow and
                  "Remove" on the second, with the pair that belongs together
                  split across the break.
                */}
                <div className="photo-actions">
                  {/* Pending is worth showing: until it uploads, this photo
                      exists only on this device. */}
                  {!uploaded && <span className="muted">waiting</span>}

                  {/*
                    One label, whatever the state.
                    It used to read "Make primary" or "Primary" depending, which
                    changed the width of the row's widest control every time one
                    was pressed. `aria-pressed` carries the state, as it does for
                    every other toggle here, and the stylesheet draws it.
                  */}
                  <button
                    type="button"
                    aria-pressed={primary}
                    onClick={() =>
                      onWrite([event('photo.primarySet', item.itemId, { photoId: photo.photoId })])
                    }
                  >
                    Primary
                  </button>

                  {/* The two arrows are one control: where this photo sits. */}
                  <div className="segmented">
                    <button
                      type="button"
                      aria-label={`Move photo ${index + 1} earlier`}
                      disabled={index === 0}
                      onClick={() => move(photo.photoId, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={`Move photo ${index + 1} later`}
                      disabled={index === ordered.length - 1}
                      onClick={() => move(photo.photoId, 1)}
                    >
                      ↓
                    </button>
                  </div>

                  <button
                    type="button"
                    className="danger"
                    aria-label={`Remove photo ${index + 1}`}
                    onClick={() =>
                      onWrite([event('photo.removed', item.itemId, { photoId: photo.photoId })])
                    }
                  >
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
